import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, PaperPlaneRight, Sparkle } from '@phosphor-icons/react';
import EllaAvatar from 'assets/ella.png';

interface QuickEllaPromptProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Quick Ella Prompt Modal
 *
 * Een kleine snelle prompt waar je een vraag kunt stellen aan Ella
 * en vervolgens wordt doorgestuurd naar de Ella chat pagina met je vraag.
 *
 * Triggered by CMD+E (Mac) or CTRL+E (Windows/Linux)
 */
export const QuickEllaPrompt: React.FC<QuickEllaPromptProps> = ({ isOpen, onClose }) => {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();

  // Auto-focus when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Navigate to Ella with the question
  const handleSubmit = () => {
    if (!input.trim()) return;

    // Navigate to Ella chat with the question as state
    navigate('/ella', { state: { initialQuestion: input.trim() } });

    // Close modal and reset
    setInput('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center pt-32 bg-black/30 animate-in fade-in duration-200">
      <div className="w-full max-w-2xl mx-4 rounded-xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-top-4 duration-300 ease-out">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-moss-primary to-moss-primary/80 border-b border-moss-primary/20">
          <div className="flex items-center gap-3">
            <img src={EllaAvatar} alt="Ella" className="w-8 h-8 rounded-full" />
            <div>
              <h3 className="text-base font-semibold text-white">Vraag het aan Ella</h3>
              <p className="text-xs text-white/70">Druk op Enter om te versturen</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Input Area */}
        <div className="p-4">
          <div className="relative">
            <textarea
              ref={textareaRef}
              className="w-full border border-neutral-primary/20 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-moss-primary/20"
              placeholder="Stel een vraag aan Ella..."
              value={input}
              rows={3}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-neutral-secondaryMid flex items-center gap-1">
              <Sparkle className="w-3 h-3" weight="fill" />
              <span>Sneltoets: {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+E</span>
            </div>
            <button
              onClick={handleSubmit}
              disabled={!input.trim()}
              className="px-4 py-2 rounded-lg bg-moss-primary text-white hover:bg-moss-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <span>Vraag Ella</span>
              <PaperPlaneRight className="w-4 h-4" weight="fill" />
            </button>
          </div>

          {/* Example queries */}
          <div className="mt-4 pt-4 border-t border-neutral-primary/10">
            <p className="text-xs font-semibold text-neutral-secondaryMid mb-2">Voorbeelden:</p>
            <div className="flex flex-wrap gap-2">
              {[
                'Hoeveel open tickets heb ik?',
                'Toon tickets van deze week',
                'Maak een ticket voor @EMIE-12',
              ].map((example) => (
                <button
                  key={example}
                  onClick={() => setInput(example)}
                  className="text-xs px-2 py-1 bg-moss-primary/10 text-moss-primary rounded-md hover:bg-moss-primary/20 transition-colors"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
